import { useCallback, useState } from 'react'

export const DEFAULT_TABLE_PAGE_SIZE = 10

interface UseTablePaginationIProps {
  /**
   * Starting rows per page. Defaults to `DEFAULT_TABLE_PAGE_SIZE`; pass a value
   * when a table follows a feature-specific pagination standard.
   */
  defaultPageSize?: number
  /** Row count *after* filtering — what the pagination footer counts. */
  totalRows: number
}

interface UseTablePaginationResultIProps {
  pageCount: number
  /** Already clamped to the available pages — safe to slice with directly. */
  pageIndex: number
  pageSize: number
  resetToFirstPage: () => void
  setPageIndex: (pageIndex: number) => void
  /** Also returns to the first page, since the old offset is meaningless. */
  setPageSize: (pageSize: number) => void
}

/**
 * Client-side page-index/page-size state for the dark `TablePagination` footer,
 * for tables that hold every row in memory and slice locally.
 *
 * The returned `pageIndex` is **derived and pre-clamped** rather than corrected
 * through an effect: a deletion, a refetch, or a newly-typed search term can
 * shrink the page count below the current index, and correcting that in a
 * `useEffect` renders the stale page once and flashes an empty table before the
 * second pass fixes it. Deriving it is right on the first render.
 *
 * Slicing deliberately stays at the call site — sections disagree on whether it
 * belongs in the section or the table, and unifying that is not this hook's job.
 *
 * Not for server-side pagination: see `@/hooks/usePagination` for the
 * TanStack + refetch flavour, which is a different problem.
 */
export const useTablePagination = ({
  defaultPageSize = DEFAULT_TABLE_PAGE_SIZE,
  totalRows,
}: UseTablePaginationIProps): UseTablePaginationResultIProps => {
  const [requestedPageIndex, setRequestedPageIndex] = useState(0)
  const [pageSize, setStatePageSize] = useState(defaultPageSize)

  const pageCount = Math.ceil(totalRows / pageSize)
  const pageIndex = Math.min(requestedPageIndex, Math.max(pageCount - 1, 0))

  const setPageSize = useCallback((nextPageSize: number) => {
    setStatePageSize(nextPageSize)
    setRequestedPageIndex(0)
  }, [])

  const resetToFirstPage = useCallback(() => setRequestedPageIndex(0), [])

  return {
    pageCount,
    pageIndex,
    pageSize,
    resetToFirstPage,
    setPageIndex: setRequestedPageIndex,
    setPageSize,
  }
}
